import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterContentBlockSaveRequest } from '@models/water-information';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageFieldComponent } from '@shared/components/image-picker/image-field.component';

@Component({
  selector: 'app-water-information-content-blocks-section',
  templateUrl: './water-information-content-blocks-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    TranslatePipe,
    ImageFieldComponent,
  ],
})
export class WaterInformationContentBlocksSectionComponent {
  readonly contentBlocks = input.required<WaterContentBlockSaveRequest[]>();
  readonly contentBlocksChange = output<WaterContentBlockSaveRequest[]>();
  readonly blockImageSelected = output<{ key: string; file: File }>();
  readonly blockImageCleared = output<string>();

  private readonly pendingImages = signal<Map<string, { name: string; url: string }>>(new Map());

  private getKey(block: WaterContentBlockSaveRequest): string | null {
    if (block.id) return String(block.id);
    if (block.clientId) return block.clientId;
    return null;
  }

  getPendingImageName(block: WaterContentBlockSaveRequest): string | null {
    const key = this.getKey(block);
    return key ? (this.pendingImages().get(key)?.name ?? null) : null;
  }

  getPendingImageUrl(block: WaterContentBlockSaveRequest): string | null {
    const key = this.getKey(block);
    return key ? (this.pendingImages().get(key)?.url ?? null) : null;
  }

  addContentBlock(): void {
    this.contentBlocksChange.emit([
      ...this.contentBlocks(),
      {
        id: null,
        clientId: crypto.randomUUID(),
        type: 'text',
        anchorNl: null,
        anchorEn: null,
        anchorDe: null,
        anchorIt: null,
        anchorFr: null,
        titleNl: null,
        titleEn: null,
        titleDe: null,
        titleIt: null,
        titleFr: null,
        textNl: null,
        textEn: null,
        textDe: null,
        textIt: null,
        textFr: null,
        videoNl: null,
        videoEn: null,
        videoDe: null,
        videoIt: null,
        videoFr: null,
        iframeTitleNl: null,
        iframeTitleEn: null,
        iframeTitleDe: null,
        iframeTitleIt: null,
        iframeTitleFr: null,
        iframeTextNl: null,
        iframeTextEn: null,
        iframeTextDe: null,
        iframeTextIt: null,
        iframeTextFr: null,
        iframeNl: null,
        iframeEn: null,
        iframeDe: null,
        iframeIt: null,
        iframeFr: null,
        imageName: null,
        imageUrl: null,
      },
    ]);
  }

  removeContentBlock(index: number): void {
    const block = this.contentBlocks()[index];
    const key = this.getKey(block);
    if (key) {
      const pending = this.pendingImages().get(key);
      if (pending) {
        URL.revokeObjectURL(pending.url);
        this.pendingImages.update((m) => {
          const n = new Map(m);
          n.delete(key);
          return n;
        });
      }
      this.blockImageCleared.emit(key);
    }
    this.contentBlocksChange.emit(this.contentBlocks().filter((_, i) => i !== index));
  }

  setType(index: number, value: string): void {
    this.contentBlocksChange.emit(this.contentBlocks().map((b, i) => (i === index ? { ...b, type: value } : b)));
  }

  selectImage(index: number, file: File): void {
    const block = this.contentBlocks()[index];
    const key = this.getKey(block);
    if (!key) return;
    const existing = this.pendingImages().get(key);
    if (existing) URL.revokeObjectURL(existing.url);
    const url = URL.createObjectURL(file);
    this.pendingImages.update((m) => new Map(m).set(key, { name: file.name, url }));
    this.blockImageSelected.emit({ key, file });
  }

  clearImage(index: number): void {
    const block = this.contentBlocks()[index];
    const key = this.getKey(block);
    if (key && this.pendingImages().has(key)) {
      const pending = this.pendingImages().get(key);
      if (pending) URL.revokeObjectURL(pending.url);
      this.pendingImages.update((m) => {
        const n = new Map(m);
        n.delete(key);
        return n;
      });
      this.blockImageCleared.emit(key);
    } else {
      this.contentBlocksChange.emit(this.contentBlocks().map((b, i) => (i === index ? { ...b, imageName: null } : b)));
    }
  }
}
